/**
 * The map at a width where columns and lines cannot be read: the same nodes
 * as two stacked lists, with the same active state. Selecting a member marks
 * the funders it shares; selecting a funder marks the members it reaches.
 */
import type { MapLayout } from './layout.js';

const connectedTo = (layout: MapLayout, active: string | null): Set<string> => {
  const out = new Set<string>();
  if (active === null) {
    return out;
  }
  for (const edge of layout.edges) {
    if (edge.funder === active) {
      out.add(edge.member);
    } else if (edge.member === active) {
      out.add(edge.funder);
    }
  }
  return out;
};

export const SharedFunderList = ({ layout, active, onPin }: { layout: MapLayout; active: string | null; onPin: (key: string | null) => void }) => {
  const connected = connectedTo(layout, active);
  const item = (key: string, label: string, degree: number, kind: 'funder' | 'member') => (
    <li key={key}>
      <button
        type="button"
        aria-pressed={active === key}
        onClick={() => onPin(active === key ? null : key)}
        className={`w-full rounded px-2 py-1 text-left ${active === key ? 'bg-teal-700 text-white' : connected.has(key) ? 'bg-teal-50 text-teal-900' : 'text-stone-800'}`}
      >
        {label} <span className={active === key ? 'text-teal-100' : 'text-stone-500'}>· {degree} {kind === 'funder' ? 'reached' : 'shared'}</span>
      </button>
    </li>
  );
  return (
    <div className="grid grid-cols-1 gap-4 text-sm" data-testid="map-list">
      <div>
        <h3 className="mb-1 font-medium text-stone-800">Funders</h3>
        <ul className="space-y-0.5">{layout.funders.map((node) => item(node.key, node.label, node.degree, 'funder'))}</ul>
      </div>
      <div>
        <h3 className="mb-1 font-medium text-stone-800">Organizations that share them</h3>
        <ul className="space-y-0.5">{layout.members.map((node) => item(node.key, node.label, node.degree, 'member'))}</ul>
      </div>
    </div>
  );
};
